import axios from 'axios';
import qs from 'qs';
import EventEmmiter from '../../../providers/event.emmiter';
import loginService from '../login.service';
import { openContactsGateAndWait } from '../contacts.gate.bridge';
import { shouldRetryTransient, nextDelayMs, retryAfterSecondsOf } from './transient.retry';
import {
    ORBITA_DISCONNECTED_CODE,
    ORBITA_DISCONNECTED_EVENT,
    ORBITA_DISCONNECTED_STATUS
} from '../../constant/crm/crm.orbita.connection.constant';

export const events = new EventEmmiter();

const CONTACTS_GATE_CODE = 'LINK_BLOCKED_BY_CONTACTS_GATE';
const DOCUMENT_VERIFICATION_PENDING_CODE = 'DOCUMENT_VERIFICATION_PENDING';
const DOCUMENT_VERIFICATION_PENDING_STATUS = 409;
const DEFAULT_RETRY_AFTER_MS = 2000;
const MAX_VERIFICATION_RETRIES = 15;
const JOB_POLL_INTERVAL_MS = 1000;
const JOB_TIMEOUT_MS = 120000;
const FINISHED_JOB_STATES = ['done', 'failed'];

export const REQUEST_TIMEOUT_IN_MILLISECONDS = 60000;

export const api = (type = 'user', additionalConfigs = {}) => {
    const paths = {
        'user': process.env.REACT_APP_API_URL || 'https://api.facilitajuridico.com'
    };

    const API = axios.create({
        baseURL: paths[type],
        timeout: REQUEST_TIMEOUT_IN_MILLISECONDS,
        headers: {
            'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
            'key': 'AIzaSyAIBy-H_w3KK1E2VuU-SMVzo6JZa3Oz3KM'
        },
        ...additionalConfigs,
    });

    // Return all http/s codes to response
    API.interceptors.response.use(undefined, (err) => {
        return Promise.reject({
            status: err.response?.status,
            config: err.config,
            response: err.response,
            code: err.code,
            message: err.message,
        });
    });

    // Inject header Authorization
    API.interceptors.request.use(
        (config) => {
            if (config.url.indexOf('users/authenticate') != -1) return config;

            const token = loginService.getToken();
            if (token) config.headers['api-key'] = `${token}`;
            return config;

            //Authorization
        },
        (error) => {
            return Promise.reject(error);
        },
    );

    return API;
};

export const get = async (config = {}) => {
    const { type, service, queryString, additionalConfigs, silent } = config;

    let queryStr = '';
    if (Object.keys(queryString || []).length > 0)
        queryStr = `?${qs.stringify(queryString, { encodeValuesOnly: true })}`;

    return await wrapperRequest(api(type, additionalConfigs).get(`${service}${queryStr}`), { silent });
};

export const put = async (config = {}) => {
    const { type, service, data } = config;
    return await wrapperRequest(api(type).put(service, data));
};

export const DOCUMENT_JOB_HEADERS = { 'x-document-job': '1' };

const readJob = (jobId) =>
    get({ type: 'user', service: `document-jobs/${jobId}` }).then(response => response && response.data && response.data.value);

export const followJob = async (jobId) => {
    const deadline = Date.now() + JOB_TIMEOUT_MS;

    while (Date.now() < deadline) {
        const documentJob = await readJob(jobId);

        if (documentJob && FINISHED_JOB_STATES.includes(documentJob.state))
            return documentJob;

        await waitBeforeRetry(JOB_POLL_INTERVAL_MS);
    }

    return null;
};

export const post = async (config = {}) => {
    const { type, service, data, params, additionalConfigs, headers } = config;
    return await wrapperRequest(api(type, additionalConfigs).post(service, data, { params, headers }));
};

export const patch = async (config = {}) => {
    const { type, service, data } = config;
    return await wrapperRequest(api(type).patch(service, data));
};

export const remove = async (params = {}) => {
    const { type, service, data } = params;
    return await wrapperRequest(api(type).delete(service, data));
};

const handleUnauthorized = (error) => {

    const requestUrl = error.config?.url || '';
    const isAuthenticationRequest = requestUrl.includes('users/authenticate');
    const wasLoggedIn = loginService.isLogin();

    if (!wasLoggedIn || isAuthenticationRequest)
        return;

    const userId = loginService.getUserAuthentication()?.id;
    loginService.saveAccess(userId);
    loginService.signOut();
    // Só a rota: o corpo da requisição pode trazer segredo (senha do certificado, semente do código de acesso).
    console.error('Sessão expirada', requestUrl);
    window.dispatchEvent(new Event('auth:unauthorized'));
}

// silent: checagem de fundo, sem acender a barra de carregamento.
const wrapperRequest = async (requestPromise, { silent = false } = {}) => {

    try {

        if (!silent) events.emmit('onLoad', true);
        let response = await requestPromise;
        if (!silent) events.emmit('onLoad', false);

        return response;

    } catch (error) {

        if (!silent) events.emmit('onLoad', false);

        if (error.status === 401)
            handleUnauthorized(error);

        if (isOrbitaDisconnected(error))
            events.emmit(ORBITA_DISCONNECTED_EVENT, true);

        if (isContactsGateBlock(error)) {
            error.config._contactsGateRetried = true;
            const verified = await openContactsGateAndWait();
            if (verified)
                return await wrapperRequest(api().request(error.config));
        }

        if (isDocumentVerificationPending(error)) {
            error.config._verificationRetries = (error.config._verificationRetries || 0) + 1;
            await waitBeforeRetry(error.response?.data?.retryAfterMs);
            return await wrapperRequest(api().request(error.config));
        }

        if (shouldRetryTransient(error))
            return await retryTransient(error);

        throw error;
    }
};

const retryTransient = async (error) => {
    error.config._transientStartedAt = error.config._transientStartedAt ?? Date.now();
    const attempt = error.config._transientRetries || 0;
    error.config._transientRetries = attempt + 1;
    await waitBeforeRetry(nextDelayMs(attempt, Math.random, retryAfterSecondsOf(error)));
    return await wrapperRequest(api().request(error.config));
};

const waitBeforeRetry = (retryAfterMs) =>
    new Promise(resolve => setTimeout(resolve, retryAfterMs || DEFAULT_RETRY_AFTER_MS));

const isDocumentVerificationPending = (error) =>
    error.status === DOCUMENT_VERIFICATION_PENDING_STATUS
    && error.response?.data?.errorCode === DOCUMENT_VERIFICATION_PENDING_CODE
    && error.config
    && (error.config._verificationRetries || 0) < MAX_VERIFICATION_RETRIES;

const isOrbitaDisconnected = (error) =>
    error.status === ORBITA_DISCONNECTED_STATUS
    && error.response?.data?.code === ORBITA_DISCONNECTED_CODE;

const isContactsGateBlock = (error) =>
    error.status === 412
    && error.response?.data?.gate === CONTACTS_GATE_CODE
    && error.config
    && !error.config._contactsGateRetried;

const extractValueByKey = (key, response = { value: {} }, defaultReturn = []) => {
    if (!response.data[key]) return defaultReturn;

    return response.data[key];
};

export default {
    api,
    get,
    post,
    put,
    patch,
    remove,
    followJob,
    extractValueByKey,
};
